import * as React from "react"
import { Link } from "@tanstack/react-router"
import { Check, Copy, LockKeyhole } from "lucide-react"
import { toast } from "sonner"

import { Button } from "@/components/ui/button"
import { DEMO_CREDENTIALS } from "@/lib/constants"

export function AuthPanel({
  eyebrow,
  title,
  intro,
  children,
}: {
  eyebrow: string
  title: string
  intro: string
  children: React.ReactNode
}) {
  return (
    <div className="field-container grid gap-10 py-12 lg:grid-cols-[minmax(0,1fr)_480px] lg:py-20">
      <section className="relative overflow-hidden border-2 border-foreground bg-foreground p-8 text-white lg:p-12">
        <div className="relative">
          <p className="field-label text-white/55">Public access card</p>
          <h1 className="mt-4 max-w-2xl font-heading text-6xl leading-[0.9] font-semibold uppercase sm:text-7xl">
            Open the complete demo workspace.
          </h1>
          <p className="mt-6 max-w-xl text-base leading-7 text-white/65">
            Compare member and administrator workflows using shared fictional
            accounts. Member reset clears only that account&apos;s saved
            activity; administrator edits affect the shared catalogue and are
            not restored by that reset.
          </p>
          <ul className="mt-10 grid gap-3 sm:grid-cols-2">
            {[
              "Save properties and named searches",
              "Manage alerts and profile details",
              "Create and archive owner listings",
              "Operate users, content, and inquiries",
            ].map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm">
                <span className="grid size-6 place-items-center bg-accent text-foreground">
                  <Check className="size-4" />
                </span>
                {item}
              </li>
            ))}
          </ul>
          <DemoCredentials />
        </div>
      </section>
      <section className="field-panel self-start p-6 sm:p-8">
        <div className="flex items-center gap-3">
          <span className="grid size-10 place-items-center bg-secondary text-primary">
            <LockKeyhole className="size-5" />
          </span>
          <div>
            <p className="field-label">{eyebrow}</p>
            <h2 className="font-heading text-3xl font-semibold uppercase">
              {title}
            </h2>
          </div>
        </div>
        <p className="mt-4 text-sm leading-6 text-muted-foreground">{intro}</p>
        <div className="mt-7">{children}</div>
      </section>
    </div>
  )
}

export function DemoCredentials({
  onChoose,
}: {
  onChoose?: (email: string, password: string) => void
}) {
  const copy = async (email: string, password: string) => {
    await navigator.clipboard.writeText(`${email}\n${password}`)
    toast.success("Demo credentials copied.")
  }

  return (
    <div className="mt-10 border border-white/30">
      <p className="border-b border-white/30 bg-white/5 px-4 py-3 font-heading text-xs font-bold tracking-widest text-white/60 uppercase">
        Published demo credentials
      </p>
      {Object.values(DEMO_CREDENTIALS).map((account) => (
        <div
          key={account.email}
          className="grid gap-3 border-b border-white/20 p-4 last:border-b-0 sm:grid-cols-[1fr_auto]"
        >
          <div className="min-w-0">
            <p className="text-xs text-accent">{account.label}</p>
            <p className="mt-1 truncate font-mono text-xs">{account.email}</p>
            <p className="font-mono text-xs">{account.password}</p>
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="field-button border-white/40 bg-transparent text-white hover:bg-white hover:text-foreground"
            onClick={() =>
              onChoose
                ? onChoose(account.email, account.password)
                : void copy(account.email, account.password)
            }
          >
            <Copy />
            {onChoose ? "Use" : "Copy"}
          </Button>
        </div>
      ))}
    </div>
  )
}

export function AuthLinks({ mode }: { mode: "login" | "register" | "reset" }) {
  return (
    <div className="mt-6 flex flex-wrap justify-between gap-3 border-t pt-5 text-sm">
      {mode !== "login" ? (
        <Link to="/login" search={{ next: undefined }} className="field-link">
          Sign in
        </Link>
      ) : (
        <Link to="/forgot-password" className="field-link">
          Forgot password?
        </Link>
      )}
      {mode !== "register" ? (
        <Link to="/register" className="field-link">
          Create a demo profile
        </Link>
      ) : null}
    </div>
  )
}
